import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useColorScheme } from '@mui/material/styles';

import MDEditor from '@uiw/react-md-editor';
import { useAuth } from 'contexts/AuthContext';

import { EditorViewMode } from 'components/editor/ViewModeToggle';
import { buildKnzTemplate } from 'components/knz/constants';
import { ScreenKey } from 'constant/ScreenPermission';
import { extractKnzFrontMatterMeta } from 'pages/devdocs/article/knzFrontMatterSync';
import Forbidden from 'pages/error/Forbidden';
import {
  buildFileRefResolver,
  parseQuestionKnz,
} from 'pages/training-admin/components/questionKnzSync';
import { listArticle, reflectArticle } from 'stores/ArticleStoreAdapter';
import {
  CodeExecutionResultSummary,
  deleteCodeExecutionResult,
  executeCode,
  getCodeExecutionResult,
  listCodeExecutionResult,
} from 'stores/CodeExecuteStoreAdapter';
import { syncQuestion } from 'stores/TrainingQuestionStoreAdapter';
import {
  FileDetailResult,
  FileExtensionResult,
  WorkSpaceNode,
  createFile,
  createFolder,
  deleteNode,
  getAdminWorkSpace,
  getFileDetail,
  getWorkSpace,
  listFileExtension,
  moveNode,
  renameNode,
  revertFile,
  saveFile,
} from 'stores/WorkSpaceStoreAdapter';
import { loadWorkspaceTabs, saveWorkspaceTabs } from 'stores/local/WorkspaceTabsLocalAdapter';
import { errorDefault, successDefault, toErrorDetail } from 'utils/Toastify';

import ActivityBar, { ActivityView } from './components/ActivityBar';
import ArgumentsChip from './components/ArgumentsChip';
import ConfirmDialog from './components/ConfirmDialog';
import ConflictDialog from './components/ConflictDialog';
import EditorArea from './components/EditorArea';
import ExecutionResultModal, { ExecutionResultView } from './components/ExecutionResultModal';
import ExplorerPanel from './components/ExplorerPanel';
import FloatingActionButtons from './components/FloatingActionButtons';
import MoveConfirmDialog from './components/MoveConfirmDialog';
import NameInputDialog from './components/NameInputDialog';
import NodeContextMenu, { ContextMenuState } from './components/NodeContextMenu';
import RunHistoryPanel from './components/RunHistoryPanel';
import TabBar, { OpenTab } from './components/TabBar';
import UnsavedCloseDialog from './components/UnsavedCloseDialog';
import {
  TreeNodeData,
  buildPathLabel,
  buildTree,
  collectFolderIds,
  extractExtension,
  isSelfOrDescendant,
  resolveCreateParentId,
} from './components/treeUtils';

/** 開いているファイルの状態 */
type FileState = {
  fileId: string;
  name: string;
  extension: string;
  editorLanguage: string;
  runnable: boolean;
  isMarkdown: boolean;
  /** knz ファイルか（専用エディタ／ビューワーに切り替える） */
  isKnz: boolean;
  /** 保存済み内容（未保存判定の基準） */
  savedContent: string;
  /** 現在のエディタ内容 */
  content: string;
  version: number;
  /** md / knz の表示モード（edit / split / preview）。それ以外の拡張子では未使用 */
  viewMode: EditorViewMode;
  /** ファイル作成日時（knz 記事ヘッダー用） */
  createdAt: string;
  /** ファイル更新日時（knz 記事ヘッダー用） */
  updatedAt: string;
  /** その fileId を参照する自分の記事IDリスト（空でなければ記事公開済み。削除不可判定に使用） */
  articleIds: string[];
  /** その fileId が設問knzとして利用されている場合の設問ID（null なら設問化されていない・API6106 v5） */
  questionId: string | null;
  /** 実行時引数の入力行（argv 順）。前回実行（lastArguments）を復元。実行可能ファイルのみ使用 */
  argumentLines: string[];
};

type ErrorLike = { status?: number; message?: string };

const isConflict = (e: unknown): boolean => (e as ErrorLike)?.status === 422;

/** 固定表示するガイドタブの識別子（実ファイルではないため通常の fileId とは区別する） */
const GUIDE_TAB_ID = '__guide__';

/** ガイドタブに表示するショートカット一覧（Markdown） */
const GUIDE_MARKDOWN = `# ワークスペースの使い方

VSCode ベースのコードエディタです。左のエクスプローラーでファイルを作成・編集し、\`.java\` などの実行可能ファイルはコードを実行できます。

## キーボードショートカット

エディタにフォーカスがある状態で利用できます。

| 操作 | Windows / Linux | Mac |
|------|-----------------|-----|
| 保存 | \`Ctrl\` + \`S\` | \`⌘\` + \`S\` |
| 実行 | \`Ctrl\` + \`E\` | \`⌘\` + \`E\` |
| プレビュー / 編集 切替（Markdown・knz） | \`Ctrl\` + \`P\` | \`⌘\` + \`P\` |
| 分割表示 切替（Markdown・knz） | \`Ctrl\` + \`\\\` | \`⌘\` + \`\\\` |
| コピー | \`Ctrl\` + \`C\` | \`⌘\` + \`C\` |
| 切り取り | \`Ctrl\` + \`X\` | \`⌘\` + \`X\` |
| 貼り付け | \`Ctrl\` + \`V\` | \`⌘\` + \`V\` |
| 元に戻す | \`Ctrl\` + \`Z\` | \`⌘\` + \`Z\` |
| やり直し | \`Ctrl\` + \`Shift\` + \`Z\` | \`⌘\` + \`Shift\` + \`Z\` |
| 検索 | \`Ctrl\` + \`F\` | \`⌘\` + \`F\` |
| 次の一致に選択追加（マルチカーソル） | \`Ctrl\` + \`D\` | \`⌘\` + \`D\` |
| 行コメント切り替え | \`Ctrl\` + \`/\` | \`⌘\` + \`/\` |
| 定義へジャンプ（knz） | \`F12\` / \`Ctrl\` + \`B\` | \`F12\` / \`⌘\` + \`B\` |

> **保存**・**実行**・**表示モード切替** はアプリ独自のショートカットです。保存は未保存の変更がある時のみ、実行は実行可能な拡張子（\`.java\` など）のファイルを開いている時のみ動作します。

## knz の定義へジャンプ

\`.knz\` ファイルでは、\`F12\` または \`Ctrl\`(\`⌘\`) + \`B\` で参照先へジャンプできます。

| カーソル位置 | ジャンプ先 |
|------|------|
| \`children:\` 配下の \`-id\` | 同じ id を持つブロックの定義（同一ファイル内で移動） |
| ブロックの \`id:\` | その id を参照している \`children\` 行（参照元へ移動） |
| \`CodeBlock\` の \`link:\` の値（fileId） | 参照先ファイルを新しいタブで開いてアクティブ化 |

- \`children\` の id ジャンプは、その id を \`id:\` に持つブロックの開始行へカーソルを移動します（定義へ移動）。
- \`id:\` の行でジャンプすると、その id を \`children\` で参照している行へ移動します（参照元へ移動）。**複数の \`children\` から参照されている場合は一覧（ピーク）が開き、選んで移動できます。**
- \`CodeBlock\` の \`link\` ジャンプは、参照している別ファイルをワークスペース内で開きます（既に開いていればそのタブをアクティブにします）。

## knz の編集支援

\`.knz\` ファイルでは、エディタが以下の支援機能を提供します。

| 機能 | 使い方 | 内容 |
|------|--------|------|
| ホバー | 要素にマウスを重ねる | ブロック名・param・\`@ユーザー\`・\`children\` の \`-id\` にカーソルを合わせると説明や参照先を表示 |
| アウトライン | 右クリック →「概要」/ \`Ctrl\`(\`⌘\`) + \`Shift\` + \`O\` | ブロックの一覧・階層を表示。クリックでその位置へ移動 |
| 折りたたみ | 行番号横の \`∨\` / \`Ctrl\`(\`⌘\`) + \`Shift\` + \`[\` | ブロック（\`<--- … --->\` 〜 \`<--- !… --->\`）や frontmatter を折りたたみ |
| 参照の検索 | \`Shift\` + \`F12\` | \`id:\` または \`children\` の \`-id\` 上で、その id を参照している全 \`children\` 行を一覧表示 |

- **ホバー**：ブロックの開始/閉じタグでは registry の説明と param 一覧、param キー行ではその param の型・必須・説明、\`@userId\` や \`{{mention:…}}\` では表示名、\`children\` の \`-id\` では参照先ブロックの種別と見出しを表示します。
- **アウトライン**：\`title\` / \`label\` があるブロックは見出しとして表示し、\`children\` 参照でツリー化されます。長い記事の見通しに便利です。
- **参照の検索**：定義へジャンプ（\`F12\` / \`Ctrl\`・\`⌘\` + \`B\`）と対で使えます。ジャンプが「1か所へ移動」なのに対し、参照の検索は「参照している全箇所を一覧」します。

## 表示モード（Markdown・knz）

\`.md\` / \`.knz\` ファイルは、エディタ上部のツールバーで **エディタ / 分割 / プレビュー** を切り替えられます。

| モード | 内容 |
|------|------|
| エディタ | コードエディタのみ |
| 分割 | 左にエディタ、右にプレビュー（左右分割） |
| プレビュー | プレビューのみ |

- \`⌘\`(または \`Ctrl\`) + \`P\`：エディタ ⇄ プレビューを切り替え（分割中に押すとプレビューへ）。
- \`⌘\`(または \`Ctrl\`) + \`\\\`：分割表示のオン/オフ。
- ツールバーは上部の \`∧\` で隠せます（隠したバーをクリックで再表示）。

## knz の書き方

\`.knz\` はブロック記法で独自コンポーネントを埋め込める記事フォーマットです。記法・コンポーネントの一覧・書き方・サンプルは **knz リファレンス**（\`/workspace/knz-reference\`）にまとまっています。初めて書くときはリファレンスの「はじめに」から順に読むと迷いません。行頭を \`//\` にするとコメント（プレビュー非表示）になる、といった記法の詳細もリファレンス側にあります。
`;

/**
 * コードエディタ画面（VSCode ベースの3領域）。
 * - :userId ありの場合は管理者用APIで対象ユーザーのワークスペースを表示（デフォルト編集不可）
 * - :userId なしの場合は本人のワークスペース
 */
const WorkspacePage: React.FC = () => {
  const { userId } = useParams<{ userId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  // クエリで開くファイル（?fileId=xxx）。開いていればアクティブ化、なければ新しく開く。
  const queryFileId = searchParams.get('fileId');
  const navigate = useNavigate();
  const { userSession, users } = useAuth();
  // マイワークスペースは権限なし（全認証済みユーザー）
  const allowed = userSession.allowedScreens.includes(ScreenKey.TRAINING_WORKSPACE);
  // 他ユーザーのワークスペースを閲覧できるのは ADMIN / DEVELOPER のみ
  const isAdmin = userSession.allowedScreens.includes(ScreenKey.TRAINING_ADMIN_WORKSPACE);

  // 対象ユーザー（管理者閲覧時）。自分自身の場合は本人扱い。
  const isOwnWorkspace = !userId || userId === userSession.userId;

  // --- 状態 ---
  const [activeView, setActiveView] = React.useState<ActivityView>('explorer');
  const [sidebarVisible, setSidebarVisible] = React.useState(true);
  const [userMenuAnchor, setUserMenuAnchor] = React.useState<HTMLElement | null>(null);
  // ワークスペース切替ポップアップのユーザー絞り込みキーワード
  const [userFilter, setUserFilter] = React.useState('');

  const [nodes, setNodes] = React.useState<WorkSpaceNode[]>([]);
  const [treeLoading, setTreeLoading] = React.useState(false);
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = React.useState<string | null>(null);

  const [extensions, setExtensions] = React.useState<FileExtensionResult[]>([]);

  // 固定のガイドタブ（常に先頭・閉じる不可）
  const guideTab: OpenTab = React.useMemo(
    () => ({
      fileId: GUIDE_TAB_ID,
      name: 'ガイド',
      dirty: false,
      pinned: true,
      icon: <MenuBookIcon sx={{ fontSize: 16 }} />,
    }),
    [],
  );

  const [tabs, setTabs] = React.useState<OpenTab[]>([guideTab]);
  const [files, setFiles] = React.useState<Record<string, FileState>>({});
  const [activeFileId, setActiveFileId] = React.useState<string | null>(GUIDE_TAB_ID);

  // user_profile からのタブ復元は初回のツリー取得後に 1 回だけ行う（マイワークスペースのみ）
  const tabsRestoredRef = React.useRef(false);
  // 復元完了を state 化する（query-fileId effect の順序保証に使う。ref 直接参照だと再評価されないため）
  const [tabsRestored, setTabsRestored] = React.useState(false);
  // クエリ ?fileId=xxx の処理済みフラグ（同じ fileId の再処理を防ぐ）
  const queryOpenedRef = React.useRef<string | null>(null);

  // Markdown プレビュー（ガイドタブ）のダークモード対応
  const { mode, systemMode } = useColorScheme();
  const resolvedMode = mode === 'system' ? systemMode : mode;
  const isDarkMode = resolvedMode === 'dark';

  // 他人のワークスペースはデフォルト編集不可。編集可能ボタンで解除できる。
  const [editUnlocked, setEditUnlocked] = React.useState(false);
  const editable = isOwnWorkspace || editUnlocked;

  // 実行履歴
  const [history, setHistory] = React.useState<CodeExecutionResultSummary[]>([]);
  const [historyLoading, setHistoryLoading] = React.useState(false);

  const [busy, setBusy] = React.useState(false);
  // コード実行中フラグ（実行中はエディタ上にサーキュラーを表示）
  const [running, setRunning] = React.useState(false);
  // 実行中の経過秒数
  const [elapsed, setElapsed] = React.useState(0);

  // 実行中は経過秒数を1秒ごとにカウントアップする
  React.useEffect(() => {
    if (!running) {
      setElapsed(0);
      return;
    }
    setElapsed(0);
    const timer = setInterval(() => setElapsed((prev) => prev + 1), 1000);
    return () => clearInterval(timer);
  }, [running]);

  // ダイアログ状態
  const [createDialog, setCreateDialog] = React.useState<{
    type: 'folder' | 'file';
    parentId: string | null;
    /** ファイル作成時の固定拡張子（'java' など。拡張子選択メニューで選ばれた値） */
    extension?: string;
  } | null>(null);
  const [renameDialog, setRenameDialog] = React.useState<WorkSpaceNode | null>(null);
  const [dialogError, setDialogError] = React.useState<string | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = React.useState<WorkSpaceNode | null>(null);
  /** 記事として公開済みのファイル fileId 集合（ツリーのバッジ表示・削除不可判定に使用） */
  const [articleFileIds, setArticleFileIds] = React.useState<Set<string>>(new Set());
  /** 記事公開済みファイルの削除がブロックされたことを知らせるダイアログ対象 */
  const [articleBlockedTarget, setArticleBlockedTarget] = React.useState<WorkSpaceNode | null>(
    null,
  );
  const [revertSeq, setRevertSeq] = React.useState<number | null>(null);
  const [deleteHistorySeq, setDeleteHistorySeq] = React.useState<number | null>(null);
  const [closeTarget, setCloseTarget] = React.useState<string | null>(null);
  const [conflictOpen, setConflictOpen] = React.useState(false);
  const [resultView, setResultView] = React.useState<ExecutionResultView | null>(null);
  const [resultOpen, setResultOpen] = React.useState(false);
  // 結果モーダルの表示元（実行直後 or 履歴クリック）
  const [resultMode, setResultMode] = React.useState<'run' | 'history'>('run');
  const [contextMenu, setContextMenu] = React.useState<ContextMenuState | null>(null);

  // --- D&D 移動 ---
  /** ドラッグ中のノード id */
  const [draggingId, setDraggingId] = React.useState<string | null>(null);
  /** ドロップ先ハイライト中のフォルダ id */
  const [dropTargetId, setDropTargetId] = React.useState<string | null>(null);
  /** 移動確認ダイアログ対象（移動対象ノード + 新親 id + 表示用の移動先パス） */
  const [moveConfirm, setMoveConfirm] = React.useState<{
    node: WorkSpaceNode;
    newParentId: string | null;
    targetPath: string;
  } | null>(null);

  const activeFile = activeFileId ? files[activeFileId] : null;

  // --- ツリー取得 ---
  const loadTree = React.useCallback(async () => {
    setTreeLoading(true);
    try {
      const result = isOwnWorkspace
        ? await getWorkSpace()
        : await getAdminWorkSpace(userId as string);
      setNodes(result);
      // デフォルトで全フォルダを展開した状態にする
      setExpandedIds(new Set(collectFolderIds(result)));
    } catch (e) {
      errorDefault('ワークスペースの取得に失敗しました');
    } finally {
      setTreeLoading(false);
    }
  }, [isOwnWorkspace, userId]);

  // 拡張子マスタ取得
  React.useEffect(() => {
    listFileExtension()
      .then(setExtensions)
      .catch(() => errorDefault('拡張子マスタの取得に失敗しました'));
  }, []);

  // 記事公開済みファイルの fileId 集合を取得（マイワークスペースのみ・ツリーのバッジ表示用）。
  // 自分が編集可能な記事の fileId を収集する（バックエンドが fileId を返す場合のみ有効。
  // 返さない場合はファイルを開いた際の articleIds で補完される）。
  React.useEffect(() => {
    if (!isOwnWorkspace) return;
    listArticle({ grouping: true })
      .then((res) => {
        const ids = res.articles
          .filter((a) => a.editable && a.fileId)
          .map((a) => a.fileId as string);
        if (ids.length > 0) {
          setArticleFileIds((prev) => {
            const next = new Set(prev);
            ids.forEach((id) => next.add(id));
            return next;
          });
        }
      })
      .catch(() => {
        // 取得失敗はバッジ表示のベストエフォートのため無視（削除ガードは getFileDetail で権威的に行う）
      });
  }, [isOwnWorkspace]);

  // 対象ワークスペースが変わったらリセットして再取得（ガイドタブは常に残す）
  React.useEffect(() => {
    setTabs([guideTab]);
    setFiles({});
    setActiveFileId(GUIDE_TAB_ID);
    setSelectedId(null);
    setExpandedIds(new Set());
    setEditUnlocked(false);
    setHistory([]);
    tabsRestoredRef.current = false;
    setTabsRestored(false);
    queryOpenedRef.current = null;
    loadTree();
  }, [loadTree, guideTab]);

  const tree = React.useMemo(() => buildTree(nodes), [nodes]);

  // --- 実行履歴取得 ---
  const loadHistory = React.useCallback(async (fileId: string) => {
    setHistoryLoading(true);
    try {
      const result = await listCodeExecutionResult(fileId);
      setHistory(result);
    } catch (e) {
      errorDefault('実行履歴の取得に失敗しました');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // アクティブファイルが実行可能なら履歴表示ビューで自動取得
  React.useEffect(() => {
    if (activeView === 'history' && activeFile && activeFile.runnable) {
      loadHistory(activeFile.fileId);
    }
  }, [activeView, activeFile, loadHistory]);

  // --- 拡張子情報の解決 ---
  const resolveExtInfo = React.useCallback(
    (ext: string): FileExtensionResult | undefined =>
      extensions.find((e) => e.extension === ext.toLowerCase()),
    [extensions],
  );

  // --- ファイルを開く ---
  const openFile = React.useCallback(
    async (node: WorkSpaceNode) => {
      if (node.type !== 'FILE' || !node.fileId) return;
      const fileId = node.fileId;
      // 既に開いていればアクティブ化
      if (files[fileId]) {
        setActiveFileId(fileId);
        return;
      }
      try {
        const detail: FileDetailResult = await getFileDetail(fileId);
        const ext = detail.extension?.toLowerCase() ?? '';
        const extInfo = resolveExtInfo(ext);
        const runnable = detail.languageId != null;
        const editorLanguage = extInfo?.editorLanguage ?? 'plaintext';
        const isMarkdown = editorLanguage === 'markdown';
        const isKnz = editorLanguage === 'knz';
        const savedContent = detail.detail ?? '';
        // 新規 knz ファイル（内容が空）はデフォルト frontmatter 入りのテンプレートで開始する。
        // savedContent は空のままにして未保存扱いとし、保存を促す。
        const initialContent =
          isKnz && savedContent.trim() === '' ? buildKnzTemplate(userSession.userId) : savedContent;
        const state: FileState = {
          fileId,
          name: node.name,
          extension: ext,
          editorLanguage,
          runnable,
          isMarkdown,
          isKnz,
          savedContent,
          content: initialContent,
          version: detail.version,
          viewMode: 'edit',
          createdAt: detail.createdAt,
          updatedAt: detail.updatedAt,
          articleIds: detail.articleIds ?? [],
          questionId: detail.questionId ?? null,
          // 前回実行の引数（半角スペース区切り）を行に復元。null / 空なら空配列
          argumentLines: detail.lastArguments ? detail.lastArguments.split(' ') : [],
        };
        setFiles((prev) => ({ ...prev, [fileId]: state }));
        setTabs((prev) => [...prev, { fileId, name: node.name, dirty: false, path: detail.path }]);
        setActiveFileId(fileId);
        // 記事公開済みなら fileId を記録（ツリーのバッジ表示用）
        if ((detail.articleIds ?? []).length > 0) {
          setArticleFileIds((prev) => {
            const next = new Set(prev);
            next.add(fileId);
            return next;
          });
        }
      } catch (e) {
        errorDefault('ファイルを開けませんでした');
      }
    },
    [files, resolveExtInfo, userSession.userId],
  );

  // --- タブ状態の復元（マイワークスペースのみ・初回ツリー取得後に 1 回） ---
  // 復元は user_profile(AUTO, "workspace-tabs") から取得する。
  // 完了時に tabsRestored=true にして query-fileId effect の順序を保証する（復元→クエリ）。
  React.useEffect(() => {
    // 他人のワークスペースは復元しないが、query-fileId が復元完了を待てるよう完了扱いにする
    if (!isOwnWorkspace) {
      if (!tabsRestoredRef.current) {
        tabsRestoredRef.current = true;
        setTabsRestored(true);
      }
      return;
    }
    if (tabsRestoredRef.current) return;
    if (treeLoading) return; // ツリー取得中は待つ
    if (nodes.length === 0) return; // 取得前 / 空ワークスペースは復元不要
    tabsRestoredRef.current = true;

    // 保存された fileId 順に、存在する FILE ノードを開く
    (async () => {
      try {
        const saved = await loadWorkspaceTabs();
        if (!saved) return;
        for (const fileId of saved.openFileIds) {
          const node = nodes.find((n) => n.type === 'FILE' && n.fileId === fileId);
          if (node) {
            // openFile 内で既存タブはスキップされる
            // eslint-disable-next-line no-await-in-loop
            await openFile(node);
          }
        }
        // アクティブタブを復元（ガイドタブ or 実在する開いたファイル）
        if (saved.activeId === GUIDE_TAB_ID) {
          setActiveFileId(GUIDE_TAB_ID);
        } else if (saved.activeId && saved.openFileIds.includes(saved.activeId)) {
          const node = nodes.find((n) => n.type === 'FILE' && n.fileId === saved.activeId);
          if (node) setActiveFileId(saved.activeId);
        }
      } finally {
        // 復元完了（保存なし・失敗を含む）を通知し、query-fileId 処理を解禁する
        setTabsRestored(true);
      }
    })();
  }, [isOwnWorkspace, treeLoading, nodes, openFile]);

  // --- タブ状態の保存（マイワークスペースのみ・復元完了後） ---
  // user_profile はサーバAPIのため、タブ/アクティブ変化のたびの過剰リクエストを
  // デバウンス（500ms）で抑制する。保存失敗はアダプタ側で握りつぶす。
  React.useEffect(() => {
    if (!isOwnWorkspace) return;
    if (!tabsRestored) return; // 復元前の初期値で上書きしない
    const openFileIds = tabs.filter((t) => t.fileId !== GUIDE_TAB_ID).map((t) => t.fileId);
    const timer = setTimeout(() => {
      saveWorkspaceTabs({ openFileIds, activeId: activeFileId });
    }, 500);
    return () => clearTimeout(timer);
  }, [isOwnWorkspace, tabsRestored, tabs, activeFileId]);

  // --- クエリ ?fileId=xxx で指定されたファイルを開く（開いていればアクティブ化） ---
  // 別画面（KNZ設問の「workspaceで編集」等）から特定ファイルを開いた状態で遷移するために使う。
  React.useEffect(() => {
    if (!queryFileId) return;
    if (queryOpenedRef.current === queryFileId) return; // 同じ指定は一度だけ処理
    if (treeLoading) return; // ツリー取得中は待つ
    if (nodes.length === 0) return; // ツリー未取得
    if (!tabsRestored) return; // タブ復元完了を待つ（復元→クエリの順序を保証し二重オープンを防ぐ）

    const node = nodes.find((n) => n.type === 'FILE' && n.fileId === queryFileId);
    queryOpenedRef.current = queryFileId;
    if (node) {
      openFile(node);
    } else {
      errorDefault('指定されたファイルが見つかりません');
    }
    // 処理後は URL からクエリを除去（タブ切替時の再処理・URL汚染を防ぐ）
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('fileId');
        return next;
      },
      { replace: true },
    );
  }, [queryFileId, treeLoading, nodes, openFile, setSearchParams, tabsRestored]);

  // --- エディタ変更 ---
  const handleEditorChange = (value: string) => {
    if (!activeFileId) return;
    setFiles((prev) => {
      const f = prev[activeFileId];
      if (!f) return prev;
      return { ...prev, [activeFileId]: { ...f, content: value } };
    });
    setTabs((prev) =>
      prev.map((t) =>
        t.fileId === activeFileId
          ? { ...t, dirty: value !== files[activeFileId]?.savedContent }
          : t,
      ),
    );
  };

  // --- 保存 ---
  // silent=true の場合は成功トーストを出さない（実行前の自動保存など）
  const doSave = React.useCallback(
    async (fileId: string, silent = false): Promise<boolean> => {
      const f = files[fileId];
      if (!f) return false;
      setBusy(true);
      try {
        // R-11: 保存/反映分離。保存（API6107 v3）は純粋な下書き保存で、記事メタ・設問の同期は行わない。
        // 記事同期は `[記事に反映]`（API6211）、設問同期は `[設問に反映]`（API6316）の明示操作で行う。
        await saveFile({ fileId, detail: f.content, version: f.version });
        setFiles((prev) => ({
          ...prev,
          [fileId]: { ...prev[fileId], savedContent: f.content, version: prev[fileId].version + 1 },
        }));
        setTabs((prev) => prev.map((t) => (t.fileId === fileId ? { ...t, dirty: false } : t)));
        if (!silent) {
          successDefault('保存しました');
        }
        return true;
      } catch (e) {
        if (isConflict(e)) {
          setConflictOpen(true);
        } else {
          errorDefault('保存に失敗しました', toErrorDetail(e));
        }
        return false;
      } finally {
        setBusy(false);
      }
    },
    [files],
  );

  // --- 記事に反映（R-11・API6211）: 保存とは別の明示操作 ---
  // フロントが knz FrontMatter をパースして記事メタを抽出し、反映APIに構造化データを送る。
  const doReflectArticle = React.useCallback(
    async (fileId: string) => {
      const f = files[fileId];
      if (!f) return;
      setBusy(true);
      try {
        // 未保存の下書きがあれば先に保存してから反映する
        if (f.content !== f.savedContent) {
          const saved = await doSave(fileId, true);
          if (!saved) return;
        }
        const meta = extractKnzFrontMatterMeta(f.content);
        if (!meta.title) {
          errorDefault('反映に失敗しました', 'knz の frontmatter に title がありません');
          return;
        }
        await reflectArticle({
          fileId,
          title: meta.title,
          subtitle: meta.subtitle || undefined,
          description: meta.description || undefined,
          visibility: meta.visibility ?? undefined,
          authorUserIds: meta.authorUserIds.length > 0 ? meta.authorUserIds : undefined,
        });
        successDefault('記事に反映しました');
      } catch (e) {
        errorDefault('記事への反映に失敗しました', toErrorDetail(e));
      } finally {
        setBusy(false);
      }
    },
    [files, doSave],
  );

  // --- 設問に反映（R-11・API6316）: 保存とは別の明示操作 ---
  // フロントが knz をパースして title / AnswerCode / Expected を抽出し、反映APIに構造化データを送る。
  const doReflectQuestion = React.useCallback(
    async (fileId: string) => {
      const f = files[fileId];
      if (!f) return;
      setBusy(true);
      try {
        // 未保存の下書きがあれば先に保存してから反映する
        if (f.content !== f.savedContent) {
          const saved = await doSave(fileId, true);
          if (!saved) return;
        }
        // knz に書かれたパスを workspace ツリーで fileId に解決してからパースする
        const resolver = buildFileRefResolver(nodes);
        const parsed = parseQuestionKnz(f.content, resolver);
        if (parsed.errors.length > 0) {
          errorDefault('設問への反映に失敗しました', parsed.errors.join('\n'));
          return;
        }
        await syncQuestion({
          sourceFileId: fileId,
          title: parsed.title,
          answerCodes: parsed.answerCodes,
        });
        successDefault('設問に反映しました');
      } catch (e) {
        errorDefault('設問への反映に失敗しました', toErrorDetail(e));
      } finally {
        setBusy(false);
      }
    },
    [files, doSave, nodes],
  );

  // --- 実行時引数の更新 ---
  const setArgumentLines = React.useCallback((fileId: string, lines: string[]) => {
    setFiles((prev) => {
      const f = prev[fileId];
      if (!f) return prev;
      return { ...prev, [fileId]: { ...f, argumentLines: lines } };
    });
  }, []);

  // --- 実行（保存 → 実行） ---
  const doRun = React.useCallback(async () => {
    if (!activeFile) return;
    // 実行前の自動保存はトーストを出さない
    const saved = await doSave(activeFile.fileId, true);
    if (!saved) return;
    setRunning(true);
    try {
      // 引数行をトリムし空行を除外して配列で送る（全空なら送らない＝引数なし実行）
      const args = activeFile.argumentLines.map((l) => l.trim()).filter((l) => l !== '');
      const result = await executeCode(activeFile.fileId, args);
      // 実行直後の結果（seq なし）を表示する
      setResultView({
        statusId: result.statusId,
        statusDescription: result.statusDescription,
        stdout: result.stdout,
        stderr: result.stderr,
        compileOutput: result.compileOutput,
        message: result.message,
        time: result.time,
        wallTime: result.wallTime,
        memory: result.memory,
        exitCode: result.exitCode,
        exitSignal: result.exitSignal,
        arguments: result.arguments,
      });
      setResultMode('run');
      setResultOpen(true);
      // 履歴を再取得
      loadHistory(activeFile.fileId);
    } catch (e) {
      errorDefault('コードの実行に失敗しました', toErrorDetail(e));
    } finally {
      setRunning(false);
    }
  }, [activeFile, doSave, loadHistory]);

  // --- タブを閉じる ---
  const closeTab = (fileId: string, force = false) => {
    // ガイドタブは閉じられない
    if (fileId === GUIDE_TAB_ID) return;
    const tab = tabs.find((t) => t.fileId === fileId);
    if (tab?.dirty && !force) {
      setCloseTarget(fileId);
      return;
    }
    setTabs((prev) => prev.filter((t) => t.fileId !== fileId));
    setFiles((prev) => {
      const next = { ...prev };
      delete next[fileId];
      return next;
    });
    if (activeFileId === fileId) {
      const remaining = tabs.filter((t) => t.fileId !== fileId);
      // 残りタブの末尾（なければガイドタブ）をアクティブにする
      setActiveFileId(remaining.length > 0 ? remaining[remaining.length - 1].fileId : GUIDE_TAB_ID);
    }
    setCloseTarget(null);
  };

  // --- 作成/リネーム/削除 ---
  // 上部アイコン: 選択中ノードを起点に作成
  const handleNewFolder = () => {
    setDialogError(undefined);
    setCreateDialog({ type: 'folder', parentId: resolveCreateParentId(selectedId, nodes) });
  };
  const handleNewFile = (extension: string) => {
    setDialogError(undefined);
    setCreateDialog({
      type: 'file',
      parentId: resolveCreateParentId(selectedId, nodes),
      extension,
    });
  };

  // コンテキストメニュー: 右クリックしたノードを起点に作成
  const handleContextCreateFolder = () => {
    setDialogError(undefined);
    setCreateDialog({ type: 'folder', parentId: resolveCreateParentId(selectedId, nodes) });
  };
  const handleContextCreateFile = (extension: string) => {
    setDialogError(undefined);
    setCreateDialog({
      type: 'file',
      parentId: resolveCreateParentId(selectedId, nodes),
      extension,
    });
  };

  const submitCreate = async (name: string) => {
    if (!createDialog) return;
    setDialogError(undefined);
    // ファイルはフロント側で拡張子チェック
    if (createDialog.type === 'file') {
      const ext = extractExtension(name);
      if (!ext) {
        setDialogError('拡張子を含むファイル名を入力してください');
        return;
      }
      if (!extensions.some((e) => e.extension === ext)) {
        setDialogError(`作成可能な拡張子ではありません（${extensionLabel}）`);
        return;
      }
    }
    setBusy(true);
    try {
      if (createDialog.type === 'folder') {
        await createFolder({ parentId: createDialog.parentId, name });
        setCreateDialog(null);
        if (createDialog.parentId) {
          setExpandedIds((prev) => new Set(prev).add(createDialog.parentId as string));
        }
        await loadTree();
        successDefault('作成しました');
      } else {
        // 拡張子マスタの template を initialDetail として渡す（実行可能拡張子のみ非 null）
        const ext = extractExtension(name);
        const template = ext ? (resolveExtInfo(ext)?.template ?? undefined) : undefined;
        const newId = await createFile({
          parentId: createDialog.parentId,
          name,
          initialDetail: template ?? undefined,
        });
        setCreateDialog(null);
        if (createDialog.parentId) {
          setExpandedIds((prev) => new Set(prev).add(createDialog.parentId as string));
        }
        // ツリーを再取得し、作成したノードを自動で開く（API6106。テンプレート入り本文が表示される）
        const refreshed = isOwnWorkspace
          ? await getWorkSpace()
          : await getAdminWorkSpace(userId as string);
        setNodes(refreshed);
        setExpandedIds(new Set(collectFolderIds(refreshed)));
        const created = refreshed.find((n) => n.id === newId);
        if (created && created.type === 'FILE') {
          await openFile(created);
        }
        successDefault('作成しました');
      }
    } catch (e) {
      const msg = (e as ErrorLike)?.message;
      setDialogError(msg || '作成に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  const submitRename = async (name: string) => {
    if (!renameDialog) return;
    setDialogError(undefined);
    setBusy(true);
    try {
      await renameNode({ id: renameDialog.id, name });
      // 開いているタブ名も更新
      if (renameDialog.fileId && files[renameDialog.fileId]) {
        setFiles((prev) => ({
          ...prev,
          [renameDialog.fileId as string]: { ...prev[renameDialog.fileId as string], name },
        }));
        setTabs((prev) => prev.map((t) => (t.fileId === renameDialog.fileId ? { ...t, name } : t)));
      }
      setRenameDialog(null);
      await loadTree();
      successDefault('名前を変更しました');
    } catch (e) {
      const msg = (e as ErrorLike)?.message;
      setDialogError(msg || '名前の変更に失敗しました');
    } finally {
      setBusy(false);
    }
  };

  // 削除要求（コンテキストメニュー）。ファイルは記事公開済みか権威的に確認し、公開済みなら削除を拒否する。
  const handleRequestDelete = async (node: WorkSpaceNode) => {
    if (node.type === 'FILE' && node.fileId) {
      // 既知の記事ファイルなら即ブロック
      if (articleFileIds.has(node.fileId)) {
        setArticleBlockedTarget(node);
        return;
      }
      // 未確認のファイルは getFileDetail で articleIds を権威的に確認
      try {
        const detail = await getFileDetail(node.fileId);
        if ((detail.articleIds ?? []).length > 0) {
          setArticleFileIds((prev) => {
            const next = new Set(prev);
            next.add(node.fileId as string);
            return next;
          });
          setArticleBlockedTarget(node);
          return;
        }
      } catch {
        // 確認に失敗した場合は通常フローに進む（削除API側でも整合が取られる）
      }
    }
    setDeleteTarget(node);
  };

  const submitDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteNode({ id: deleteTarget.id });
      // ファイル削除時は開いているタブも閉じる
      if (deleteTarget.fileId && files[deleteTarget.fileId]) {
        closeTab(deleteTarget.fileId, true);
      }
      setDeleteTarget(null);
      await loadTree();
      successDefault('削除しました');
    } catch (e) {
      const msg = (e as ErrorLike)?.message;
      errorDefault(msg || '削除に失敗しました');
      setDeleteTarget(null);
    } finally {
      setBusy(false);
    }
  };

  // --- 履歴クリック → 詳細モーダル ---
  const handleClickResult = async (seq: number) => {
    if (!activeFile) return;
    try {
      const detail = await getCodeExecutionResult(activeFile.fileId, seq);
      setResultView({ ...detail });
      setResultMode('history');
      setResultOpen(true);
    } catch (e) {
      errorDefault('実行結果の取得に失敗しました');
    }
  };

  // --- リバート ---
  const submitRevert = async () => {
    if (!activeFile || revertSeq == null) return;
    setBusy(true);
    try {
      await revertFile({ fileId: activeFile.fileId, seq: revertSeq, version: activeFile.version });
      // ファイル詳細を再取得してエディタに反映
      const detail = await getFileDetail(activeFile.fileId);
      setFiles((prev) => ({
        ...prev,
        [activeFile.fileId]: {
          ...prev[activeFile.fileId],
          savedContent: detail.detail ?? '',
          content: detail.detail ?? '',
          version: detail.version,
        },
      }));
      setTabs((prev) =>
        prev.map((t) => (t.fileId === activeFile.fileId ? { ...t, dirty: false } : t)),
      );
      setRevertSeq(null);
      loadHistory(activeFile.fileId);
      successDefault('ファイルを戻しました');
    } catch (e) {
      setRevertSeq(null);
      if (isConflict(e)) {
        setConflictOpen(true);
      } else {
        errorDefault('リバートに失敗しました', toErrorDetail(e));
      }
    } finally {
      setBusy(false);
    }
  };

  // --- 履歴削除 ---
  const submitDeleteHistory = async () => {
    if (!activeFile || deleteHistorySeq == null) return;
    setBusy(true);
    try {
      await deleteCodeExecutionResult({ fileId: activeFile.fileId, seq: deleteHistorySeq });
      setDeleteHistorySeq(null);
      loadHistory(activeFile.fileId);
      successDefault('実行履歴を削除しました');
    } catch (e) {
      setDeleteHistorySeq(null);
      errorDefault('実行履歴の削除に失敗しました', toErrorDetail(e));
    } finally {
      setBusy(false);
    }
  };

  // --- 競合解消: 最新取得 ---
  const handleReloadConflict = async () => {
    if (!activeFile) {
      setConflictOpen(false);
      return;
    }
    try {
      const detail = await getFileDetail(activeFile.fileId);
      setFiles((prev) => ({
        ...prev,
        [activeFile.fileId]: {
          ...prev[activeFile.fileId],
          savedContent: detail.detail ?? '',
          content: detail.detail ?? '',
          version: detail.version,
        },
      }));
      setTabs((prev) =>
        prev.map((t) => (t.fileId === activeFile.fileId ? { ...t, dirty: false } : t)),
      );
    } catch (e) {
      errorDefault('最新の取得に失敗しました');
    } finally {
      setConflictOpen(false);
    }
  };

  // --- 全て開く/閉じる ---
  const handleExpandAll = () => setExpandedIds(new Set(collectFolderIds(nodes)));
  const handleCollapseAll = () => setExpandedIds(new Set());

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleContextMenu = (e: React.MouseEvent, node: TreeNodeData) => {
    e.preventDefault();
    setSelectedId(node.id);
    setContextMenu({ mouseX: e.clientX + 2, mouseY: e.clientY - 6, nodeId: node.id });
  };

  // --- D&D 移動 ---
  /**
   * ドラッグ中ノードを指定フォルダへドロップ可能か（フロント抑止）。
   * - フォルダのみ受け入れ（ファイルへのドロップ不可。呼び出し側は FOLDER のみ渡す）
   * - 自分自身/子孫の配下への移動は不可（循環）
   * - 現在の親と同じフォルダは冪等（受け入れるが移動時は何もしない）
   */
  const canDropOn = React.useCallback(
    (targetFolderId: string): boolean => {
      if (!draggingId) return false;
      // 自分自身/子孫フォルダの配下へは不可（循環）
      if (isSelfOrDescendant(draggingId, targetFolderId, nodes)) return false;
      return true;
    },
    [draggingId, nodes],
  );

  /** ドロップ確定（フォルダ配下 or ルート直下）。移動先パスを算出して確認ダイアログを開く */
  const requestMove = React.useCallback(
    (newParentId: string | null) => {
      const node = draggingId ? (nodes.find((n) => n.id === draggingId) ?? null) : null;
      setDropTargetId(null);
      setDraggingId(null);
      if (!node) return;
      // 冪等: 現在の親と同じ場所へのドロップは何もしない（エラーも出さない）
      const currentParent = node.parentId ?? null;
      if (currentParent === newParentId) return;
      const targetPath = buildPathLabel(newParentId, nodes); // ルートは "/"
      setMoveConfirm({ node, newParentId, targetPath });
    },
    [draggingId, nodes],
  );

  /** 移動確認 → API6112 実行 → 成功後ツリー再取得 */
  const submitMove = async () => {
    if (!moveConfirm) return;
    setBusy(true);
    try {
      await moveNode({ id: moveConfirm.node.id, newParentId: moveConfirm.newParentId });
      setMoveConfirm(null);
      // 移動先フォルダを展開して結果を見せる
      if (moveConfirm.newParentId) {
        setExpandedIds((prev) => new Set(prev).add(moveConfirm.newParentId as string));
      }
      await loadTree();
      successDefault('移動しました');
    } catch (e) {
      // サーバ側チェック（循環・同名・ツリー整合・種別）違反も含めエラー表示
      errorDefault('移動に失敗しました', toErrorDetail(e));
      setMoveConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const setViewMode = (mode: EditorViewMode) => {
    if (!activeFileId) return;
    setFiles((prev) => ({
      ...prev,
      [activeFileId]: { ...prev[activeFileId], viewMode: mode },
    }));
  };

  const handleActivitySelect = (view: ActivityView) => {
    if (activeView === view && sidebarVisible) {
      setSidebarVisible(false);
    } else {
      setActiveView(view);
      setSidebarVisible(true);
    }
  };

  const extensionLabel = extensions.map((e) => `.${e.extension}`).join(' / ');

  // 権限がない場合（URL直打ち等）は Forbidden
  // - マイワークスペース: 全認証済みユーザー可
  // - 他人のワークスペース: ADMIN / DEVELOPER のみ
  if (!allowed || (!isOwnWorkspace && !isAdmin)) {
    return <Forbidden />;
  }

  const contextNode = contextMenu ? (nodes.find((n) => n.id === contextMenu.nodeId) ?? null) : null;
  // フォルダで配下に子ノードがある場合は削除不可
  const contextHasChildren = contextNode ? nodes.some((n) => n.parentId === contextNode.id) : false;
  // 記事として公開済みのファイルは削除不可（既知の記事ファイルのみ即時判定。未確認分は onDelete で権威的にチェック）
  const contextIsArticle = Boolean(
    contextNode?.type === 'FILE' && contextNode.fileId && articleFileIds.has(contextNode.fileId),
  );
  const disableContextDelete =
    (contextNode?.type === 'FOLDER' && contextHasChildren) || contextIsArticle;
  const disableContextDeleteReason = contextIsArticle
    ? '記事として公開されているため削除できません（記事を削除してからファイルを削除してください）'
    : '配下にファイル/フォルダがあるため削除できません';
  const dirtyTab = closeTarget ? tabs.find((t) => t.fileId === closeTarget) : null;

  // リネーム対象がファイルの場合、拡張子は固定し、入力欄にはベース名のみを表示する
  const renameExtension =
    renameDialog?.type === 'FILE' ? (extractExtension(renameDialog.name) ?? undefined) : undefined;
  const renameInitialValue =
    renameDialog && renameExtension
      ? renameDialog.name.slice(0, renameDialog.name.length - renameExtension.length - 1)
      : renameDialog?.name;

  return (
    <>
      <Box sx={{ display: 'flex', height: '100%', minHeight: 0, overflow: 'hidden' }}>
        <ActivityBar
          active={sidebarVisible ? activeView : null}
          onSelect={handleActivitySelect}
          showAdmin={isAdmin}
          isOtherWorkspace={!isOwnWorkspace}
          editable={editable}
          onOpenUserSwitch={(anchor) => setUserMenuAnchor(anchor)}
          onToggleEditable={() => setEditUnlocked((v) => !v)}
        />

        {sidebarVisible && (
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              borderRight: 1,
              borderColor: 'divider',
              minHeight: 0,
            }}
          >
            {activeView === 'explorer' ? (
              <ExplorerPanel
                tree={tree}
                loading={treeLoading}
                selectedId={selectedId}
                expandedIds={expandedIds}
                articleFileIds={articleFileIds}
                editable={editable}
                extensions={extensions}
                title={isOwnWorkspace ? 'MyWorkspace' : (userId ?? '')}
                onToggle={toggleExpand}
                onSelect={(n) => setSelectedId(n.id)}
                onOpenFile={openFile}
                onContextMenu={handleContextMenu}
                onNewFolder={handleNewFolder}
                onNewFile={handleNewFile}
                onExpandAll={handleExpandAll}
                onCollapseAll={handleCollapseAll}
                dnd={{
                  enabled: editable,
                  draggingId,
                  dropTargetId,
                  canDropOn,
                  onDragStartNode: (id) => setDraggingId(id),
                  onDragEndNode: () => {
                    setDraggingId(null);
                    setDropTargetId(null);
                  },
                  onDragOverFolder: (folderId) => setDropTargetId(folderId),
                  onDropOnFolder: (folderId) => requestMove(folderId),
                }}
                onDropToRoot={() => requestMove(null)}
              />
            ) : (
              <RunHistoryPanel
                runnable={Boolean(activeFile?.runnable)}
                results={history}
                loading={historyLoading}
                editable={editable}
                onClickResult={handleClickResult}
                onRevert={(seq) => setRevertSeq(seq)}
                onDelete={(seq) => setDeleteHistorySeq(seq)}
              />
            )}
          </Box>
        )}

        {/* 右ペイン */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <TabBar
            tabs={tabs}
            activeFileId={activeFileId}
            onSelect={setActiveFileId}
            onClose={(fileId) => closeTab(fileId)}
          />
          <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
            {activeFileId === GUIDE_TAB_ID ? (
              <Box
                data-color-mode={isDarkMode ? 'dark' : 'light'}
                sx={{
                  height: '100%',
                  overflow: 'auto',
                  p: 2.5,
                  bgcolor: 'background.paper',
                  // ガイドは全体的にコンパクトに表示する
                  '& .wmde-markdown': { fontSize: '13px', lineHeight: 1.6 },
                  '& .wmde-markdown h1': { fontSize: '1.4em' },
                  '& .wmde-markdown h2': { fontSize: '1.15em' },
                  '& .wmde-markdown h3': { fontSize: '1em' },
                  '& .wmde-markdown table': { fontSize: '0.9em' },
                }}
              >
                <Box sx={{ maxWidth: 760 }}>
                  <MDEditor.Markdown
                    source={GUIDE_MARKDOWN}
                    style={{ background: 'transparent' }}
                  />
                </Box>
              </Box>
            ) : activeFile ? (
              <>
                <EditorArea
                  language={activeFile.editorLanguage}
                  value={activeFile.content}
                  // md / knz は 3 モード対応。それ以外は 'edit' 固定。
                  viewMode={
                    activeFile.isMarkdown || activeFile.isKnz ? activeFile.viewMode : 'edit'
                  }
                  supportsViewMode={activeFile.isMarkdown || activeFile.isKnz}
                  readOnly={!editable}
                  onChange={handleEditorChange}
                  onViewModeChange={setViewMode}
                  createdAt={activeFile.createdAt}
                  updatedAt={activeFile.updatedAt}
                  // knz: CodeBlock の link 定義ジャンプ（F12 / Cmd・Ctrl+B）で
                  // その fileId のファイルを開いてアクティブ化する
                  onOpenLinkedFile={(fileId) => {
                    const node = nodes.find((n) => n.type === 'FILE' && n.fileId === fileId);
                    if (node) {
                      openFile(node);
                    } else {
                      errorDefault('参照先のファイルが見つかりません');
                    }
                  }}
                  // Cmd/Ctrl+S で保存（保存ボタンが活性な時のみ = 編集可能かつ未保存）
                  onSave={() => {
                    const canSave = editable && activeFile.content !== activeFile.savedContent;
                    if (canSave && !busy && !running) {
                      doSave(activeFile.fileId);
                    }
                  }}
                  // Cmd/Ctrl+E で実行（実行可能拡張子かつ実行中でない場合）
                  onRun={() => {
                    if (activeFile.runnable && !busy && !running) {
                      doRun();
                    }
                  }}
                />
                <FloatingActionButtons
                  runnable={activeFile.runnable}
                  supportsViewMode={activeFile.isMarkdown || activeFile.isKnz}
                  canSave={editable && activeFile.content !== activeFile.savedContent}
                  busy={busy || running}
                  onSave={() => doSave(activeFile.fileId)}
                  onRun={doRun}
                  runExtra={
                    <ArgumentsChip
                      lines={activeFile.argumentLines}
                      onChange={(lines) => setArgumentLines(activeFile.fileId, lines)}
                      disabled={busy || running}
                    />
                  }
                  // knz かつ記事化済みなら記事反映、設問化済みなら設問反映を表示（両方表示しうる・R-11）
                  onReflectArticle={
                    editable && activeFile.isKnz && activeFile.articleIds.length > 0
                      ? () => doReflectArticle(activeFile.fileId)
                      : undefined
                  }
                  onReflectQuestion={
                    editable && activeFile.isKnz && activeFile.questionId != null
                      ? () => doReflectQuestion(activeFile.fileId)
                      : undefined
                  }
                />
                {/* コード実行中のオーバーレイ */}
                {running && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 10,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1.5,
                      bgcolor: 'rgba(0, 0, 0, 0.35)',
                    }}
                  >
                    <CircularProgress />
                    <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                      実行中... {elapsed}秒
                    </Typography>
                  </Box>
                )}
              </>
            ) : (
              <Box
                sx={{
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  ファイルを選択してください
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* コンテキストメニュー */}
      <NodeContextMenu
        state={contextMenu}
        extensions={extensions}
        onClose={() => setContextMenu(null)}
        onCreateFolder={handleContextCreateFolder}
        onCreateFile={handleContextCreateFile}
        onRename={() => {
          if (contextNode) {
            setDialogError(undefined);
            setRenameDialog(contextNode);
          }
        }}
        onDelete={() => {
          if (contextNode) handleRequestDelete(contextNode);
        }}
        disableDelete={disableContextDelete}
        disableDeleteReason={disableContextDeleteReason}
      />

      {/* 新規作成ダイアログ */}
      <NameInputDialog
        open={createDialog !== null}
        title={createDialog?.type === 'folder' ? '新規フォルダ作成' : '新規ファイル作成'}
        pathLabel={createDialog ? buildPathLabel(createDialog.parentId, nodes) : undefined}
        fixedExtension={createDialog?.type === 'file' ? createDialog.extension : undefined}
        confirmLabel="作成"
        errorMessage={dialogError}
        submitting={busy}
        onClose={() => setCreateDialog(null)}
        onSubmit={submitCreate}
      />

      {/* リネームダイアログ（ファイルは拡張子固定） */}
      <NameInputDialog
        open={renameDialog !== null}
        title="名前の変更"
        fixedExtension={renameExtension}
        initialValue={renameInitialValue}
        confirmLabel="変更"
        errorMessage={dialogError}
        submitting={busy}
        onClose={() => setRenameDialog(null)}
        onSubmit={submitRename}
      />

      {/* 削除確認 */}
      <ConfirmDialog
        open={deleteTarget !== null}
        title="削除の確認"
        messages={[
          `「${deleteTarget?.name}」を削除します。`,
          'この操作は取り消せません。',
          ...(deleteTarget?.type === 'FILE' ? ['関連する実行履歴も削除されます。'] : []),
        ]}
        confirmLabel="削除"
        confirmColor="error"
        submitting={busy}
        onClose={() => setDeleteTarget(null)}
        onConfirm={submitDelete}
      />

      {/* 記事公開済みファイルの削除ブロック案内 */}
      <Dialog
        open={articleBlockedTarget !== null}
        onClose={() => setArticleBlockedTarget(null)}
        fullWidth
        maxWidth="xs"
        slotProps={{
          paper: { elevation: 0, sx: { borderRadius: 3, border: 1, borderColor: 'divider' } },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <ArticleOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" fontWeight={600}>
              削除できません
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={0.5}>
            <Typography variant="body2" color="text.secondary">
              「{articleBlockedTarget?.name}」は記事として公開されています。
            </Typography>
            <Typography variant="body2" color="text.secondary">
              記事を削除してから、ファイルを削除してください。
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="contained"
            onClick={() => setArticleBlockedTarget(null)}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* リバート確認 */}
      <ConfirmDialog
        open={revertSeq !== null}
        title="リバートの確認"
        messages={[
          `実行 #${revertSeq} の内容にファイルを戻します。`,
          '現在の編集内容は失われます。',
        ]}
        confirmLabel="戻す"
        submitting={busy}
        onClose={() => setRevertSeq(null)}
        onConfirm={submitRevert}
      />

      {/* 履歴削除確認 */}
      <ConfirmDialog
        open={deleteHistorySeq !== null}
        title="実行履歴の削除"
        messages={[`実行 #${deleteHistorySeq} の履歴を削除します。`, 'この操作は取り消せません。']}
        confirmLabel="削除"
        confirmColor="error"
        submitting={busy}
        onClose={() => setDeleteHistorySeq(null)}
        onConfirm={submitDeleteHistory}
      />

      {/* タブを閉じる確認 */}
      <UnsavedCloseDialog
        open={closeTarget !== null}
        fileName={dirtyTab?.name ?? ''}
        canSave={editable}
        onSave={async () => {
          if (closeTarget) {
            const ok = await doSave(closeTarget);
            if (ok) closeTab(closeTarget, true);
          }
        }}
        onDiscard={() => closeTarget && closeTab(closeTarget, true)}
        onCancel={() => setCloseTarget(null)}
      />

      {/* 移動確認ダイアログ（D&D） */}
      <MoveConfirmDialog
        open={moveConfirm !== null}
        name={moveConfirm?.node.name ?? ''}
        targetPath={moveConfirm?.targetPath ?? '/'}
        isFolder={moveConfirm?.node.type === 'FOLDER'}
        submitting={busy}
        onClose={() => setMoveConfirm(null)}
        onConfirm={submitMove}
      />

      {/* 競合ダイアログ */}
      <ConflictDialog open={conflictOpen} submitting={busy} onReload={handleReloadConflict} />

      {/* 実行結果モーダル */}
      <ExecutionResultModal
        open={resultOpen}
        result={resultView}
        mode={resultMode}
        onClose={() => setResultOpen(false)}
      />

      {/* ユーザー切替メニュー（管理者用） */}
      <Menu
        open={Boolean(userMenuAnchor)}
        anchorEl={userMenuAnchor}
        onClose={() => {
          setUserMenuAnchor(null);
          setUserFilter('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              minWidth: 260,
              maxHeight: 420,
            },
          },
        }}
      >
        {/* 絞り込み用テキストフィールド（虫眼鏡アイコン付き） */}
        <Box
          sx={{ px: 1.5, pt: 1, pb: 1.5 }}
          // メニューのタイプアヘッド/選択にキー入力を渡さない
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            fullWidth
            size="small"
            autoFocus
            placeholder="ユーザーを検索..."
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
        </Box>

        {/* マイワークスペースは常に表示 */}
        <MenuItem
          selected={isOwnWorkspace}
          onClick={() => {
            setUserMenuAnchor(null);
            setUserFilter('');
            navigate('/workspace');
          }}
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}>
            マイワークスペース
          </ListItemText>
        </MenuItem>
        <Divider />
        {users
          .filter((u) => u.userId !== userSession.userId)
          .filter((u) => {
            const kw = userFilter.trim().toLowerCase();
            if (!kw) return true;
            return (
              u.userName.toLowerCase().includes(kw) ||
              u.userId.toLowerCase().includes(kw) ||
              u.mailAddress.toLowerCase().includes(kw)
            );
          })
          .map((u) => (
            <MenuItem
              key={u.userId}
              selected={userId === u.userId}
              onClick={() => {
                setUserMenuAnchor(null);
                setUserFilter('');
                navigate(`/workspace/${u.userId}`);
              }}
            >
              <ListItemIcon>
                <PersonIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ variant: 'body2' }}
                secondaryTypographyProps={{ variant: 'caption' }}
                secondary={u.mailAddress}
              >
                {u.userName}
              </ListItemText>
            </MenuItem>
          ))}
      </Menu>
    </>
  );
};

export default WorkspacePage;
